import {
  buffers, type TakeableChannel,
} from 'redux-saga';
import {
  actionChannel,
  call,
  delay, put,
  race, take, takeLatest,
} from 'redux-saga/effects';
import { FetchableResourceStatus } from '~/_shared/types/fetchableResource/fetchableResource';
import { flatten } from '~/_shared/utils/collections/collections';
import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import { select } from '~/_shared/utils/saga/effects';
import { notNull } from '~/_shared/utils/typeGuards';
import { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';
import { clientIdSelector } from '../selectors/useClientIdSelector';
import { userDataIsReadySelector } from '../userData/settings/userDataSettings.selectors';
import {
  USER_DATA_SETTING_READY_SET, USER_GET_DATA_SUCCESS,
} from '../userData/userData.actionTypes';
import {
  addBoundaryStatsItems, updateBoundaryStatsItems,
} from './stats.actionCreators';
import { createLoadedStatItem } from './stats.factory';
import {
  type BoundaryStatsRequestItem,
  type BoundaryStatsResponse, getBoundaryStats,
} from './stats.repository';
import { boundaryGroupStatsSelector } from './stats.selectors';

export function* statsSagas() {
  yield takeLatest(USER_GET_DATA_SUCCESS, watchForLoadStats);
}

/*
 * When the status of any item is changed to FetchableResourceStatus.Requested,
 * this saga should handle loading if from the backend
*/
export function* watchForLoadStats() {
  // Buffer latest map settings action when the saga is busy and not taking actions.
  const statsChannel: TakeableChannel<Action> = yield actionChannel(
    [addBoundaryStatsItems.type, updateBoundaryStatsItems.type], buffers.sliding(1));

  while (true) {
    let isUserDataSettingsReady: boolean = yield select(userDataIsReadySelector);

    while (!isUserDataSettingsReady) {
      yield take(USER_DATA_SETTING_READY_SET);
      isUserDataSettingsReady = yield select(userDataIsReadySelector);
    }

    const clientId: number | null = yield select(clientIdSelector);

    if (!clientId) {
      continue;
    }

    yield take(statsChannel);

    while (true) {

      const { debounce } = yield race({
        debounce: delay(1000),
        latestAction: take(statsChannel),
      });

      if (debounce) {
        break;
      }
    }

    const boundaryGroupsStats: ReturnType<typeof boundaryGroupStatsSelector> = yield select(boundaryGroupStatsSelector);

    const statsPerBoundary = Array.from(boundaryGroupsStats.values());
    const allStats = flatten(
      statsPerBoundary.map((stats) => Array.from(stats.values()))
    );
    const statsToFetch = allStats.filter((stat) => (
      stat.data.status === FetchableResourceStatus.Requested
    ));

    if (statsToFetch.length === 0) {
      continue;
    }

    yield put(updateBoundaryStatsItems(statsToFetch.map((stat) => ({
      ...stat,
      data: {
        status: FetchableResourceStatus.Loading,
      },
    }))));

    const queryItems: ReadonlyArray<BoundaryStatsRequestItem> = statsToFetch.map((stat) => ({
      query_id: stat.id,
      boundary_group_id: stat.boundaryGroupId,
      ...(stat.type === ChartMetricType.SpreadsheetColumn ? {
        type: ChartMetricType.SpreadsheetColumn,
        spreadsheet_id: stat.column.spreadsheetId,
        metric: stat.column.columnId,
      } : {
        type: ChartMetricType.Demographic,
        metric: stat.demographicId,
      }),
    }));

    try {
      const response: BoundaryStatsResponse = yield call(getBoundaryStats, clientId, queryItems);

      const itemsToUpdate = stringObjectEntries(response.result).map(([queryId, queryItem]) => {
        const existingStat = allStats.find((stat) => stat.id === queryId);

        if (existingStat) {
          if (queryItem) {
            return createLoadedStatItem(queryId, queryItem);
          }
          else {
            return {
              ...existingStat,
              data: {
                status: FetchableResourceStatus.NotFound,
              },
            };
          }
        }

        return null;
      }).filter(notNull);

      if (itemsToUpdate.length > 0) {
        yield put(updateBoundaryStatsItems(itemsToUpdate));
      }
    }
    catch (e) {
      // set all requested stats to error state
      const itemsToUpdate = statsToFetch.map((stat) => ({
        ...stat,
        data: {
          status: FetchableResourceStatus.NotFound,
        },
      }));
      if (itemsToUpdate.length > 0) {
        yield put(updateBoundaryStatsItems(itemsToUpdate));
      }
    }
  }
}
